import React, { useState, useCallback } from 'react';
import { ReactComponent as NavigationIcClose24px } from '../../assets/icons/navigation/ic_close_24px.svg';
import PropTypes from 'prop-types';
import classNames from 'classnames';

const defaultIcons = {
  close: <NavigationIcClose24px />
};

/**
 * @typedef Props
 * @prop  {React.ReactNode} children
 * @prop  {string} [className]
 * @prop {boolean} [disabled]
 * @prop {{close: React.ReactNode}} [icons]
 * @prop {() => void} [onClick]
 * @prop {boolean} [removable]
 * @prop {'chip' | 'dot' | 'outlined'} [type]
 * @prop {'success' | 'warning' | 'error' | 'monochrome-light' | 'monochrome-dark'} [variant]
 * @prop {() => void} [onRemove]
 * @prop {React.ReactNode} [dropdown]
 * @prop {{}} [closeButtonProps]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifChip = (props) => {
  const {
    children,
    className = '',
    disabled = false,
    icons: userIcons = {},
    onClick = null,
    removable = false,
    type = 'chip',
    variant = null,
    onRemove = null,
    dropdown = null,
    closeButtonProps = {},
    ...other
  } = props;
  const [visible, setVisible] = useState(true);
  const [active, setActive] = useState(false);

  const activate = useCallback(() => setActive(true), []);
  const deactivate = useCallback(() => setActive(false), []);

  const icons = {
    ...defaultIcons,
    ...userIcons
  };

  const onRemoved = event => {
    event.stopPropagation();
    setVisible(false);
    if (onRemove) {
      onRemove();
    }
  };

  const classes = classNames({
    'motif-chip': true,
    'motif-chip-active': active,
    'motif-chip-disabled': disabled,
    'motif-chip-dropdown': dropdown,
    'motif-chip-outlined': type === 'outlined',
    'motif-ghost': disabled,
    [className]: true
  });

  const buttonClasses = classNames({
    'motif-chip-button': true,
    'motif-chip-interactive': onClick,
    'motif-chip-removable': removable,
    [`motif-chip-${variant}`]: variant
  });

  const dotClasses = classNames({
    'motif-dot': true,
    'motif-dot-disabled': disabled,
    [`motif-dot-${variant}`]: variant,
    [className]: true
  });

  const renderChip = visible && (
    <div className={classes} data-motif-chip>
      <div
        {...((removable || dropdown || onClick) && !disabled ? { tabIndex: 0 } : {})}
        {...other}
        className={buttonClasses}
        onBlur={deactivate}
        onClick={onClick}
        onFocus={activate}
        onMouseEnter={activate}
        onMouseLeave={deactivate}
      >
        {children}
      </div>
      {dropdown &&
        React.cloneElement(dropdown, {
          trigger: React.cloneElement(dropdown.props.trigger, {
            onBlur: deactivate,
            onFocus: activate,
            onMouseEnter: activate,
            onMouseLeave: deactivate
          })
        })}
      {removable && !dropdown && (
        <button
          aria-label="close chip"
          className="motif-close-icon"
          disabled={disabled}
          onBlur={deactivate}
          onClick={evt => onRemoved(evt)}
          onFocus={activate}
          onMouseEnter={activate}
          onMouseLeave={deactivate}
          {...closeButtonProps}
        >
          {icons.close}
        </button>
      )}
    </div>
  );

  const renderDot = (
    <div className={dotClasses} {...other} data-motif-chip>
      <span className="motif-dot-text">{children}</span>
    </div>
  );

  return type === 'dot' ? renderDot : renderChip;
};

MotifChip.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  closeButtonProps: PropTypes.instanceOf(Object),
  disabled: PropTypes.bool,
  icons: PropTypes.shape({
    close: PropTypes.node
  }),
  dropdown: PropTypes.node,
  onClick: PropTypes.func,
  onRemove: PropTypes.func,
  removable: PropTypes.bool,
  type: PropTypes.oneOf(['chip', 'dot', 'outlined']),
  variant: PropTypes.oneOf(['success', 'warning', 'error', 'monochrome-light', 'monochrome-dark'])
};

export { MotifChip };
